import { makeTrackFrame, trackLngPairs, footprintIsShifted } from './trackFrame.js';

const finite = (n) => Number.isFinite(n);

// True when the panel has something to draw: a cone, a track piece, a wind swath or timed wind set, a flood or quake footprint.
export function hasDrawableGeometry(parsed) {
  if (!parsed) return false;
  const t = parsed.track;
  return !!(
    parsed.cone
    || t?.segments?.length || t?.rawSegments?.length
    || Object.keys(parsed.windSwath || {}).length
    || (parsed.windByTime || []).some((w) => Object.keys(w.sets || {}).length)
    || parsed.flood?.affected
    || parsed.quake
  );
}

// -> [[minLat, minLng], [maxLat, maxLng]] in the layer's longitude frame, or null when nothing is drawable.
// TC: every track vertex + the cone (wind areas when there is no cone); FL: the affected polygon; EQ: the intensity bands (circle as fallback).
export function hazardBounds(parsed) {
  if (!parsed) return null;
  let minLat = Infinity; let minLng = Infinity; let maxLat = -Infinity; let maxLng = -Infinity;
  const addPoint = (lat, lng) => {
    if (!finite(lat) || !finite(lng)) return;
    minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
  };
  const addBbox = (b) => {                       // [minLng, minLat, maxLng, maxLat]
    if (!Array.isArray(b) || b.length < 4) return;
    addPoint(b[1], b[0]);
    addPoint(b[3], b[2]);
  };

  if (parsed.track) {
    const toFrame = makeTrackFrame(trackLngPairs(parsed.track), footprintIsShifted(parsed));
    if (parsed.track.trackIncomplete) {
      (parsed.track.rawSegments || []).forEach((s) => {
        addPoint(s.from[1], toFrame(s.from[0]));
        addPoint(s.to[1], toFrame(s.to[0]));
      });
    } else {
      (parsed.track.vertices || []).forEach((v) => addPoint(v.lat, toFrame(v.lng)));
    }
  }
  addBbox(parsed.cone?.bbox);
  if (!parsed.cone) {
    Object.values(parsed.windSwath || {}).forEach((p) => addBbox(p?.bbox));
    (parsed.windByTime || []).forEach((w) => Object.values(w.sets || {}).forEach((p) => addBbox(p?.bbox)));
  }
  addBbox(parsed.flood?.affected?.bbox);
  if (parsed.quake) {
    const bands = parsed.quake.bands || [];
    if (bands.length) bands.forEach((b) => addBbox(b.prepared?.bbox));
    else addBbox(parsed.quake.circle?.bbox);
  }
  return finite(minLat) ? [[minLat, minLng], [maxLat, maxLng]] : null;
}
